import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, FileSearch, FileText, Loader2, Lock, Palette, PencilLine, ShieldCheck, Upload, X, Zap } from 'lucide-react';
import { Badge, Button, Card, CardContent } from '@/components/ui';
import { Field, Textarea } from '@/components/ui/Field';
import { ErrorState } from '@/components/shared/EmptyState';
import { PublicShell } from '@/components/layout/PublicShell';
import { candidateApi, errorMessage, publicRequest } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { CheckResult } from './resumeCheck';
import { ReportMockup, StepLabel } from './resumeCheckDemo';
import { CvTools, FREE_LAYOUTS, Facts, HowItWorks, ResumeCheckFaq, WhatItMeasures } from './ResumeCheckFaq';
import { useSeo } from '@/lib/seo';
import { JOB_PRESETS } from './jobPresets';



/**
 * A candidate checking their own CV against a job they found anywhere.
 *
 * People arrive here cold, from a search, with one question and no patience — so the
 * tool is the first thing on the page rather than something a hero scrolls past. The
 * result opens on its own screen, because it is what they came for and what they will
 * read twice, and sharing a page with the inputs meant scrolling past their own job
 * description to re-read their score.
 *
 * The page makes one claim, and it is the one a resume-checking product cannot make:
 * this is the engine the hiring side runs, pointed the other way. No borrowed logos and
 * no invented statistics, because the honest version is stronger than either.
 */
export function ResumeCheckPage() {
  useSeo('/resume-check');

  const navigate = useNavigate();
  const { state } = useLocation();
  const fileInput = useRef<HTMLInputElement>(null);
  /**
   * A CV handed over from the builder or the layouts screen, in place of an upload:
   * the text of the document they just made, so the check reads what they will send.
   */
  const handed = (state as { cv?: { text?: unknown; name?: unknown } } | null)?.cv;
  const [file, setFile] = useState<File | null>(() =>
    handed && typeof handed.text === 'string' && handed.text.trim()
      ? new File([handed.text], typeof handed.name === 'string' ? handed.name : 'cv.txt', { type: 'text/plain' })
      : null
  );
  const fromBuilder = Boolean(handed && file);
  const [jobDescription, setJobDescription] = useState('');
  /** The typical advert in the box, while it is still the one we put there. */
  const preset = JOB_PRESETS.find((p) => p.text === jobDescription) ?? null;
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [dragging, setDragging] = useState(false);

  const enoughJob = jobDescription.trim().length >= 80;
  const ready = Boolean(file) && enoughJob;

  const check = async () => {
    if (!file) return;
    setChecking(true);
    setError(null);

    const body = new FormData();
    body.append('file', file);
    body.append('jobDescription', jobDescription.trim());

    try {
      const { data } = await candidateApi.post<CheckResult>('/resume-check', body, {
        ...publicRequest,
        headers: { ...publicRequest.headers, 'Content-Type': 'multipart/form-data' },
      });
      // Carried in navigation state rather than stored anywhere, which is the same
      // promise the page makes about the file itself.
      navigate('/resume-check/result', { state: { result: data } });
    } catch (err) {
      setError(errorMessage(err, 'We could not read that CV.'));
    } finally {
      setChecking(false);
    }
  };

  return (
    <PublicShell cvTools wide note="Nothing here is submitted to anyone, and your CV is not stored.">
      <div>
        {/* Two columns from lg: the thing you do on the left, the thing you get on the
            right. Below that width the preview moves under the form, because on a phone
            an example of the answer must not come before the question. */}
        {/* clip, not hidden: `hidden` would make this a scroll container and break
            the sticky preview beside it. Scoped to the hero, which is the only part
            that overflows on purpose — on the whole page it also clipped the
            full-bleed bands back to the measure.

            The claim and the thing it claims, on one row. The headline ran the full
            width on its own with the demo pushed below it beside the form, which left
            the top right of the page empty on a desktop screen and made the argument
            twice — once in words at the top, once in pictures further down. */}
        <header className="relative grid items-start gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
          {/* The site's graph-paper backdrop, faded out downward. */}
          <div
            aria-hidden
            className="bleed pointer-events-none absolute -top-24 -z-10 h-[720px] grid-fade opacity-40 [mask-image:linear-gradient(to_bottom,black,transparent)]"
          />
          <div className="space-y-4 motion-safe:animate-rise">
          {/* One soft wash of the brand behind the heading — depth without a gradient
              slab. Behind the text, and never in the way of a click. */}
          <div
            aria-hidden
            className="pointer-events-none absolute -left-32 -top-40 -z-10 size-[520px] rounded-full bg-brand/15 blur-[100px]"
          />
          {/* The marketing site's pill: a dot, a short claim, a hairline border. */}
          <p className="inline-flex items-center gap-2 rounded-full border border-brand/25 bg-brand-soft/60 py-1.5 pl-3 pr-4 text-[13px] font-medium text-ink">
            <span aria-hidden className="size-1.5 rounded-full bg-brand" />
            Free · No sign-up · Nothing kept unless you save it
          </p>

          {/**
           * Two-tone, the way the marketing site sets its headline: the situation in
           * ink, the thing this product does about it in the brand colour. It is the
           * one typographic move that site is recognisable by.
           */}
          {/* Split for colour only — the two halves read as the one sentence the
              build writes into the page's <h1> and its structured data. */}
          <h1 className="max-w-[14ch] text-balance font-display text-[clamp(42px,7.4vw,84px)] font-semibold leading-[0.98] tracking-[-0.025em] text-ink">
            {/* The name people search for, as the heading's first line: the title of
                the page says "ATS resume checker", and a heading that agrees with it is
                the one Google keeps instead of rewriting. */}
            <span className="mb-4 block font-sans text-xs font-bold uppercase tracking-[0.18em] text-brand">Free ATS score checker</span>
            See your CV <span className="text-brand">the way an ATS does.</span>
          </h1>

          {/* On a phone the form is a long scroll below this, and most people who land
              here from a shared link are on a phone without their CV file on it. Both
              ways forward, where the thumb already is. */}
          <div className="grid gap-3 sm:flex sm:flex-wrap lg:hidden">
            <Button size="lg" className="w-full sm:w-auto" asChild>
              <a href="#check">
                <FileSearch aria-hidden />
                Check my CV
              </a>
            </Button>
            <Button size="lg" variant="secondary" className="w-full sm:w-auto" asChild>
              <Link to="/resume-builder">
                <PencilLine aria-hidden />
                No CV file? Build one free
              </Link>
            </Button>
          </div>

          <p className="max-w-2xl text-pretty text-[20px] leading-[1.6] text-ink-muted">
            A free ATS resume checker: paste the job you are applying for — or just pick
            your role — add your CV, and get your ATS score, the requirements it could not
            find, and what to change, in about ten seconds. No CV yet? Build one in
            Hinglish. Just want it to look better? Pick a new design.
          </p>
          <p className="max-w-2xl text-[15px] leading-relaxed text-ink-subtle">
            This runs the same scoring engine companies hiring on HireOrbit use to screen
            applicants. You are seeing the side of it they see.
          </p>


          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2 text-[13px] text-ink-muted">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="size-4 text-brand" aria-hidden />
              Read and discarded
            </span>
            <span className="inline-flex items-center gap-2">
              <Zap className="size-4 text-brand" aria-hidden />
              About ten seconds
            </span>
            <span className="inline-flex items-center gap-2">
              <FileText className="size-4 text-brand" aria-hidden />
              PDF, Word or text
            </span>
            <span className="inline-flex items-center gap-2">
              <Palette className="size-4 text-brand" aria-hidden />
              {FREE_LAYOUTS} free designs
            </span>
          </div>
          </div>

          <div className="hidden lg:mt-14 lg:block">
            <ReportMockup />
          </div>
        </header>

        {/* Full width, with the two things you provide beside each other. Stacked in
            a narrow column the job box and the drop zone read as a long form; side by
            side they read as the two halves of one comparison, which is what they
            are. */}
        <div id="check" className="mt-10 scroll-mt-20">
          <div className="space-y-5">
        {/* The three reasons people arrive, before the form for the first of them:
            somebody with no CV should not have to scroll past a drop zone to find out
            this page can make one. */}
        <nav aria-label="What do you want to do?" className="grid gap-3 sm:grid-cols-3">
          <Path
            here
            icon={<FileSearch className="size-5" aria-hidden />}
            title="Check my CV against a job"
            body="Score, missing skills, changes you accept."
          />
          <Path
            to="/resume-builder"
            icon={<PencilLine className="size-5" aria-hidden />}
            title="Build a CV from scratch"
            body="Write it in English, Hindi or Hinglish."
          />
          <Path
            to="/resume-check/restyle"
            icon={<Palette className="size-5" aria-hidden />}
            title="Change my CV's design"
            body={`Same words, ${FREE_LAYOUTS} free layouts.`}
          />
        </nav>

        <Card className="rounded-[10px] border-line bg-surface shadow-none">
          <CardContent className="space-y-6 p-5 sm:p-7">
            <div className="grid gap-6 lg:grid-cols-2 lg:gap-7">
            <Field
              label={<StepLabel index={1} done={enoughJob}>The job you are applying for</StepLabel>}
              htmlFor="job-description"
              hint={
                preset
                  ? `A typical ${preset.role.toLowerCase()} advert. Your score is against this; paste the real one for a sharper match.`
                  : jobDescription.trim().length > 0 && !enoughJob
                    ? 'A little more — paste the whole advert and the match gets far more precise.'
                    : 'Paste the whole advert. Requirements, responsibilities, all of it.'
              }
            >
              {/* Most people who search for an ATS checker have a CV and no advert to
                  hand. The score is always against a job, so they pick the role and get
                  the usual advert for it, in the box where they can see and change it. */}
              <div className="space-y-2">
                <p className="text-[12.5px] text-ink-muted">No job ad? Check against a typical one:</p>
                <div className="flex flex-wrap gap-1.5">
                  {JOB_PRESETS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      aria-pressed={preset?.id === p.id}
                      onClick={() => setJobDescription(p.text)}
                      className={cn(
                        'inline-flex min-h-8 items-center rounded-full border px-3 text-[12.5px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                        preset?.id === p.id
                          ? 'border-brand bg-brand-soft text-ink'
                          : 'border-line bg-surface text-ink-muted hover:border-brand/50 hover:text-ink'
                      )}
                    >
                      {p.role}
                    </button>
                  ))}
                </div>
              </div>
              <Textarea
                id="job-description"
                rows={10}
                value={jobDescription}
                onChange={(event) => setJobDescription(event.target.value)}
                placeholder="Paste the full job description here…"
              />
            </Field>

            <Field
              label={<StepLabel index={2} done={Boolean(file)}>Your CV</StepLabel>}
              hint={
                file ? (
                  fromBuilder ? 'The CV you just made. Paste the job, and check it.' : 'Read in memory to produce the score, then discarded.'
                ) : (
                  <>
                    No CV yet?{' '}
                    <Link to="/resume-builder" className="font-medium text-brand underline underline-offset-2">
                      Build one
                    </Link>{' '}
                    — it takes about ten minutes.
                  </>
                )
              }
              className="flex min-h-0 flex-col"
            >
              {/* Out of the tab order: the drop zone below is the control, and opens
                  this. Left in, it was a second, nameless stop for the same action —
                  announced as "button" with nothing to say what it uploads. */}
              <input
                ref={fileInput}
                type="file"
                className="sr-only"
                tabIndex={-1}
                aria-label="Your CV"
                accept=".pdf,.doc,.docx,.txt,.rtf"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFile(event.target.files?.[0] ?? null)}
              />
              {file ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-[var(--radius-control)] border border-brand/40 bg-brand-soft/40 px-4 py-9 text-center">
                  <span className="grid size-11 place-items-center rounded-xl bg-brand text-white dark:text-ink-inverted">
                    <FileText className="size-5" aria-hidden />
                  </span>
                  <span className="max-w-full truncate font-display text-[16px] font-semibold text-ink">
                    {file.name}
                  </span>
                  <span className="text-[12.5px] text-ink-muted">
                    {(file.size / 1024).toFixed(0)} kB · ready
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFile(null);
                      if (fileInput.current) fileInput.current.value = '';
                    }}
                  >
                    <X />
                    Choose another
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  // Dropping a file onto the page is how most people expect to hand one
                  // over, and the click target has to keep working for everyone who
                  // does not — including anybody driving this from a keyboard.
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDragging(false);
                    const dropped = event.dataTransfer.files?.[0];
                    if (dropped) setFile(dropped);
                  }}
                  className={cn(
                    'flex w-full flex-1 flex-col items-center justify-center gap-1.5 rounded-[var(--radius-control)] border border-dashed px-4 py-9 text-center transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                    dragging
                      ? 'border-brand bg-brand-soft'
                      : 'border-line-strong hover:border-brand hover:bg-brand-soft/40'
                  )}
                >
                  <span
                    className={cn(
                      'mb-1 grid size-11 place-items-center rounded-xl transition-colors',
                      dragging ? 'bg-brand text-white dark:text-ink-inverted' : 'bg-surface-raised text-ink-subtle'
                    )}
                  >
                    <Upload className="size-5" aria-hidden />
                  </span>
                  <span className="font-display text-[16px] font-semibold text-ink">
                    {dragging ? 'Drop it here' : 'Drop your CV here'}
                  </span>
                  <span className="text-[13px] text-ink-muted">
                    or <span className="font-medium text-brand underline underline-offset-2">choose a file</span>
                  </span>
                  <span className="mt-1 text-[12px] text-ink-subtle">PDF · Word · plain text</span>
                </button>
              )}
            </Field>
            </div>

            <div className="flex flex-col gap-3 border-t border-line pt-6 sm:flex-row sm:items-center">
              <Button size="lg" onClick={check} disabled={!ready} loading={checking}>
                {checking ? 'Reading your CV…' : 'Check my CV'}
                {!checking && <ArrowRight />}
              </Button>
              <p className="flex items-center gap-1.5 text-[12.5px] text-ink-subtle">
                <Lock className="size-3.5 shrink-0" aria-hidden />
                Your CV is read and discarded. We do not store it.
              </p>
            </div>

            {checking && (
              <p className="flex items-center gap-2 text-[13px] text-ink-muted">
                <Loader2 className="size-4 shrink-0 animate-spin text-brand" aria-hidden />
                Reading your CV, then checking it against the advert line by line…
              </p>
            )}
          </CardContent>
        </Card>

        {error && <ErrorState title="That did not work" description={error} />}
        </div>

          {/* What it does, then what you get back. */}
          {/* The mockup sits beside the headline from lg up; below that it comes
              after the form, because on a phone the thing you do must come first. */}
          <div className="mt-7 lg:hidden">
            <ReportMockup />
          </div>
        </div>

        {/* Four facts between the form and the explanation: the questions somebody
            has before uploading, answered before they have to go looking. */}
        <div className="mt-16">
          <Facts />
        </div>

        <section className="pt-24 sm:pt-28">
          <CvTools />
        </section>

        <section className="bleed mt-20 border-y border-line bg-surface">
          <div className="mx-auto w-full max-w-[1240px] px-5 py-24 sm:px-8 sm:py-28">
            <WhatItMeasures />
          </div>
        </section>

        {/* The one dark band. It is where the three steps live because they are the
            page's spine, and a change of ground is the cheapest way to say so. */}
        <section className="bleed band-dark">
          <div className="mx-auto w-full max-w-[1240px] px-5 py-24 sm:px-8 sm:py-28">
            <HowItWorks inverted />
          </div>
        </section>

        <section className="pb-8 pt-24 sm:pt-28">
          <ResumeCheckFaq />
        </section>

      </div>
    </PublicShell>
  );
}

/**
 * One of the three things the page does. The one in use is marked and is not a link;
 * the others go to their own pages.
 */
function Path({ to, here, icon, title, body }: { to?: string; here?: boolean; icon: React.ReactNode; title: string; body: string }) {
  const inner = (
    <>
      <span className={cn('grid size-10 shrink-0 place-items-center rounded-xl', here ? 'bg-brand text-white dark:text-ink-inverted' : 'bg-brand-soft text-brand')}>
        {icon}
      </span>
      <span className="min-w-0 space-y-0.5">
        <span className="flex items-center gap-2 text-[14.5px] font-semibold text-ink">
          {title}
          {!here && <ArrowRight className="size-3.5 shrink-0 text-brand transition-transform group-hover:translate-x-0.5" aria-hidden />}
        </span>
        <span className="block text-[12.5px] leading-snug text-ink-muted">{here ? `${body} You are here.` : body}</span>
      </span>
    </>
  );
  const shape = 'group flex items-center gap-3 rounded-[10px] border p-3.5 transition-all';
  return here ? (
    <div aria-current="page" className={cn(shape, 'border-brand bg-brand-soft/40')}>
      {inner}
    </div>
  ) : (
    <Link
      to={to!}
      className={cn(shape, 'border-line bg-surface hover:-translate-y-0.5 hover:border-brand/50 hover:shadow-lift-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand')}
    >
      {inner}
    </Link>
  );
}
